import React from 'react'
import { shortenSido } from '../utils/address'
import TruncatedText from './TruncatedText'
import { useCategoryStore } from '../stores/categoryIconStore'
import { VisitType, getVisitTypeDisplayLabel } from '../constants/visitType'
import { getPriceRangeIcon } from '../constants/priceRange'
import { formatScoreOneDecimal } from '../utils/scoreFormat'

/** 영수증에 보여줄 키워드 최대 개수 */
const MAX_SHOWN_KEYWORDS = 3

export interface ReviewReceiptCardProps {
  placeName: string
  placeAddress: string
  placeCategory: string | null
  score: number | null
  hideScore?: boolean
  /** 서버에서 포맷한 표시 문자열이 있으면 우선 사용 (클라 Date 파싱 생략) */
  dateDisplay?: string | null
  visitDate: string | null
  createdAt: string | null
  /** 이 장소의 몇 번째 기록인지. 1이거나 없으면 표시하지 않는다 */
  visitNumber?: number | null
  visitType: VisitType
  reviewText: string | null
  taggedFriends: Array<{ id: number; nickname: string }>
  minPricePerPerson: number | null
  maxPricePerPerson: number | null
  keywords: Array<{ id: number; name: string }>
}

const ReviewReceiptCard: React.FC<ReviewReceiptCardProps> = ({
  placeName,
  placeAddress,
  placeCategory,
  score,
  hideScore = false,
  dateDisplay,
  visitDate,
  createdAt,
  visitNumber,
  visitType,
  reviewText,
  taggedFriends,
  minPricePerPerson,
  maxPricePerPerson,
  keywords,
}) => {
  const getCategoryIconBrown = useCategoryStore(
    (state) => state.getCategoryIconBrown,
  )
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${year}년 ${month}월 ${day}일`
  }

  const dateLine =
    (dateDisplay && dateDisplay.trim()) || formatDate(visitDate || createdAt)

  // 카페는 눈금이 달라 카테고리를 같이 넘긴다
  const priceRangeIcon = getPriceRangeIcon(
    minPricePerPerson,
    maxPricePerPerson,
    placeCategory
  )

  // 키워드는 앞의 몇 개만 보여준다.
  //
  // 다 쓰면 줄이 넘쳐 영수증 칸이 늘어난다. 키워드 5개짜리 리뷰에서
  // '반려동물 동반'이 두 줄로 쪼개져 내려앉았다.
  const shownKeywords = keywords.slice(0, MAX_SHOWN_KEYWORDS)
  const hasKeywords = shownKeywords.length > 0

  return (
    <div className="relative w-full">
      {/* 영수증 배경 이미지 */}
      <img
        src="/images/receipt-bg.png"
        alt="영수증 배경 이미지"
        className="absolute top-0 left-0 w-full h-full object-fill"
      />

      {/* 영수증 내용 */}
      <div className="relative z-10 flex flex-col items-center justify-center pb-87px px-26px">
        {/* 가게 이름 */}
        <p className="mt-75px font-pretendard font-bold text-20px text-brown-80%-main leading-125% text-center">
          {placeName}
        </p>

        {/* 주소 */}
        <p className="mt-12px font-pretendard font-regular text-14px leading-180% text-[#918063] text-center">
          {shortenSido(placeAddress)}
        </p>

        {/* 점수 */}
        <div className="mt-12px flex items-center justify-center gap-8px">
          {placeCategory && getCategoryIconBrown(placeCategory) && (
            <img
              src={getCategoryIconBrown(placeCategory)!}
              alt="카테고리 아이콘"
              className="w-28px h-28px object-contain"
            />
          )}
          {hideScore ? (
            <span className="font-pretendard font-bold text-20px text-brown-80%-main leading-125% text-center">
              ? 점
            </span>
          ) : (
            score !== null && (
              <span className="font-pretendard font-bold text-20px text-brown-80%-main leading-125% text-center">
                {formatScoreOneDecimal(score)} 점
              </span>
            )
          )}
        </div>

        <div className="mt-24px py-10px w-full flex gap-8px border-t border-b border-dashed border-brown-80%-main">
          {/* 날짜 — 재방문이면 앞에 회차를 붙인다 (굵게 하지 않는다: 날짜와
              같은 무게라야 한 줄로 읽힌다) */}
          <p className="flex-1 text-style-captionM text-4B3621-80% text-left">
            {visitNumber !== null &&
              visitNumber !== undefined &&
              visitNumber > 1 && <>{visitNumber}번째 기록 · </>}
            {dateLine}
          </p>

          {/* 서비스 유형 */}
          <p className="text-style-captionM text-brown-80%-main text-right">
            {getVisitTypeDisplayLabel(visitType)}
          </p>
        </div>

        {/* 한줄평 */}
        {reviewText && (
          <div className="mt-24px w-full">
            <TruncatedText
              text={reviewText}
              maxLines={3}
              moreText="... 더 보기"
              textClassName="font-pretendard font-regular text-14px leading-180% text-brown-80%-main text-left"
            />
          </div>
        )}

        {/* 친구 태그 */}
        {taggedFriends.length > 0 && (
          <div className="mt-24px w-full flex">
            <TruncatedText
              text={taggedFriends.map((friend) => friend.nickname).join(', ')}
              maxLines={1}
              moreText="..."
              tailText="님과 함께"
              textClassName="font-pretendard font-bold text-14px leading-20px text-brown-80%-main text-left"
              moreTextClassName="font-pretendard font-bold text-14px leading-20px text-brown-80%-main text-left"
              tailTextClassName="font-pretendard font-400 text-14px leading-20px text-brown-80%-main text-left"
            />
          </div>
        )}

        {/* 가격대 및 키워드 */}
        {(priceRangeIcon || hasKeywords) && (
          <div className="mt-12px py-10px w-full flex gap-8px border-t border-b border-dashed border-brown-80%-main">
            {/* 1인당 가격대 */}
            <div className="flex-1">
              {priceRangeIcon && (
                <p className="w-full text-style-captionM text-brown-80%-main text-left">
                  {priceRangeIcon}
                </p>
              )}
            </div>

            {/* 키워드 */}
            {hasKeywords && (
              <p className="text-style-captionM text-brown-80%-main text-right">
                {shownKeywords.map((keyword) => keyword.name).join(', ')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReviewReceiptCard
